import { useState, useMemo } from 'react';
import { TrendingUp, TrendingDown, ArrowUpDown, Search, Star, Zap } from 'lucide-react';
import { useLiveMarketData } from '../hooks/useLiveMarketData';
import CurrencyIcon from './CurrencyIcon';
import './MarketTable.css';

const MarketTable = () => {
  const [sortField, setSortField] = useState('marketCapRank');
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Use live market data with WebSocket
  const { 
    coins, 
    isLoading, 
    error, 
    isLive, 
    lastUpdated 
  } = useLiveMarketData({ useWebSocket: true });

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    let result = [...coins];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (coin) =>
          coin.name.toLowerCase().includes(query) ||
          coin.symbol.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    result.sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      
      // Handle undefined/null values
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      
      // String comparison
      if (typeof aVal === 'string') {
        const comparison = aVal.localeCompare(bVal);
        return sortDirection === 'asc' ? comparison : -comparison;
      }
      
      // Numeric comparison
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });

    return result.slice(0, 10);
  }, [coins, searchQuery, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Format time since last update
  const getTimeSinceUpdate = () => {
    if (!lastUpdated) return 'Never';
    const seconds = Math.floor((Date.now() - lastUpdated) / 1000);
    if (seconds < 5) return 'Just now';
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    return `${hours}h ago`;
  };

  if (isLoading) {
    return (
      <section className="market-section" id="market">
        <div className="market-loading">
          <div className="loading-spinner-large" />
          <p>Loading market data...</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="market-section" id="market">
        <div className="market-error">
          <p>Failed to load market data</p>
          <button onClick={() => window.location.reload()} className="retry-btn">
            Retry
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="market-section" id="market">
      <div className="section-container">
        <div className="section-header">
          <span className="section-label">
            <span className={`label-dot ${isLive ? 'live' : ''}`} />
            {isLive ? 'Live Market' : 'Market Data'}
          </span>
          <h2 className="section-title">
            Trending <span className="gradient-text">Cryptocurrencies</span>
          </h2>
          <p className="section-subtitle">
            Track real-time prices and market movements
            {isLive && (
              <span className="update-badge">
                Updated {getTimeSinceUpdate()}
              </span>
            )}
          </p>
        </div>

        <div className="market-card">
          <div className="market-toolbar">
            <div className="market-search">
              <Search className="search-icon" />
              <input
                type="text"
                placeholder="Search coins..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <a href="/market" className="view-all-btn">
              <Star className="btn-icon" />
              View All
            </a>
          </div>

          <div className="table-wrapper">
            <table className="market-table">
              <thead>
                <tr>
                  <th className="col-rank">
                    <button 
                      className={`sort-btn ${sortField === 'marketCapRank' ? 'active' : ''}`}
                      onClick={() => handleSort('marketCapRank')}
                    >
                      #
                      <ArrowUpDown className="sort-icon" />
                    </button>
                  </th>
                  <th className="col-name">Name</th>
                  <th 
                    className={`col-price sortable ${sortField === 'price' ? 'active' : ''}`}
                    onClick={() => handleSort('price')}
                  >
                    Price
                    <ArrowUpDown className="sort-icon" />
                  </th>
                  <th 
                    className={`col-change sortable ${sortField === 'change24h' ? 'active' : ''}`}
                    onClick={() => handleSort('change24h')}
                  >
                    24h %
                    <ArrowUpDown className="sort-icon" />
                  </th>
                  <th 
                    className={`col-volume sortable hidden-sm ${sortField === 'volume24h' ? 'active' : ''}`}
                    onClick={() => handleSort('volume24h')}
                  >
                    Volume (24h)
                    <ArrowUpDown className="sort-icon" />
                  </th>
                  <th 
                    className={`col-cap sortable hidden-md ${sortField === 'marketCap' ? 'active' : ''}`}
                    onClick={() => handleSort('marketCap')}
                  >
                    Market Cap
                    <ArrowUpDown className="sort-icon" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedData.map((coin, index) => (
                  <tr key={coin.symbol} className="coin-row">
                    <td className="col-rank">{coin.marketCapRank || index + 1}</td>
                    <td className="col-name">
                      <div className="coin-info">
                        <CurrencyIcon 
                          currency={{ id: coin.id, ticker: coin.symbol.toLowerCase(), name: coin.name }}
                          externalImage={coin.image}
                          size="md"
                        />
                        <div className="coin-details">
                          <span className="coin-name">{coin.name}</span>
                          <span className="coin-symbol">{coin.symbol}</span>
                        </div>
                      </div>
                    </td>
                    <td className="col-price">
                      <span className="price-value">{coin.formattedPrice}</span>
                    </td>
                    <td className="col-change">
                      <span className={`change-badge ${coin.change24h >= 0 ? 'positive' : 'negative'}`}>
                        {coin.change24h >= 0 ? (
                          <TrendingUp className="trend-icon" />
                        ) : (
                          <TrendingDown className="trend-icon" />
                        )}
                        {coin.change24h > 0 ? '+' : ''}{coin.change24h.toFixed(2)}%
                      </span>
                    </td>
                    <td className="col-volume hidden-sm">
                      {coin.formattedVolume}
                    </td>
                    <td className="col-cap hidden-md">
                      {coin.formattedMarketCap}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredAndSortedData.length === 0 && (
            <div className="no-results">
              <p>No cryptocurrencies found matching &quot;{searchQuery}&quot;</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default MarketTable;
